import Badge, { BadgeShade } from "@components/library/Badge";
import { ProgrammaticLogo } from "@components/ProductsLogos";
import { Popover, Transition } from "@headlessui/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  MenuIcon,
  XIcon,
} from "@heroicons/react/outline";
import { ChevronDownIcon } from "@heroicons/react/solid";
import Button, { ButtonShade } from "components/library/Button";
import IconButton, { IconButtonShade } from "components/library/IconButton";
import { ArrowUpRightIcon } from "components/library/Icons";
import { HumanloopLogo } from "components/library/Logos";
import { classNames } from "components/library/utils/classNames";
import { useBreakpoints } from "hooks/useBreakpoints";
import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useState } from "react";
import {
  CAREERS_LINK,
  HOMEPAGE_LINK,
  HUMANLOOP_APP_LOGIN,
  HUMANLOOP_DOCS_LINK,
  HUMANLOOP_TYPEFORM,
  LLM_TYPEFORM,
} from "utils/constants";

interface BaseHeaderItem {
  name: string;
}
interface NestedLink extends BaseHeaderItem {
  items: WebsiteLink[]; // Allowing only one level of nesting
}
interface HeaderLink extends BaseHeaderItem {
  href: string;
  external?: boolean;
  badge?: string;
  display?: React.ReactNode; // optional way to display the link
}

type HeaderItem = NestedLink | HeaderLink;

const isNestedLink = (item: HeaderItem): item is NestedLink => {
  return "items" in item;
};

interface WebsiteLink {
  name: string;
  href: string;
  Icon?:
    | React.FunctionComponent<React.ComponentProps<"svg">>
    | React.FunctionComponent<React.ComponentProps<"img">>;
  description?: string;
}

const headerLinks: HeaderItem[] = [
  // {
  //   name: "Products",
  //   items: [
  //     {
  //       name: "Programmatic",
  //       description: "Powerful weak labeling",
  //       href: "/programmatic",
  //       Icon: Lissajous5Icon,
  //     },
  //     {
  //       name: "Platform",
  //       description: "Annotation, training and deployment",
  //       href: "#",
  //       Icon: Lissajous2Icon,
  //     },
  //   ],
  // },
  // {
  //   name: "Programmatic",
  //   href: "/programmatic",
  //   display: (
  //     <span className="hidden items-center gap-x-8 opacity-80 hover:opacity-100 lg:flex">
  //       <ProgrammaticLogo size={20} noIcon />

  //       {/* <Badge shade={BadgeShade.Blue} className="hidden text-11-14 md:block">
  //         New
  //       </Badge> */}
  //     </span>
  //   ),
  // },
  // { name: "Pricing", href: "/pricing" },
  { name: "About", href: "/about" },

  { name: "Blog", href: "/blog" },
  {
    name: "Careers",
    href: CAREERS_LINK,
    external: true,
  },

  // { name: "Docs", href: HUMANLOOP_DOCS_LINK, external: true },
];

export const Header = () => {
  const { isMediumWidth } = useBreakpoints();
  const router = useRouter();

  return (
    <Popover className="">
      <div className="container-lg mx-auto flex w-full items-center justify-between py-20 md:py-40">
        <Button href={HOMEPAGE_LINK} styling="ghost" className="-ml-10">
          <HumanloopLogo className=" -mb-4 h-28 text-gray-900 " />
        </Button>
        <div className="hidden gap-4 opacity-90 md:flex">
          {headerLinks.map((item) =>
            isNestedLink(item) ? (
              <HeaderDropdown
                key={item.name}
                name={item.name}
                items={item.items}
              />
            ) : (
              <Button
                key={item.name}
                href={item.href}
                size={isMediumWidth ? 32 : 24}
                className="group flex-none"
                styling="ghost"
                active={router.pathname.startsWith(item.href)}
                target={item.external ? "_blank" : undefined}
                IconRight={
                  item.external
                    ? (props: any) => (
                        <div className="invisible relative top-2 right-2 transition-all  group-hover:visible group-hover:top-0 group-hover:right-0 group-hover:opacity-100">
                          <ArrowUpRightIcon {...props} />
                        </div>
                      )
                    : undefined
                }
              >
                {item.display ? item.display : item.name}
                {item.badge && (
                  <Badge shade={BadgeShade.Blue}>{item.badge}</Badge>
                )}
              </Button>
            )
          )}

          <Button
            href={LLM_TYPEFORM}
            target="_blank"
            styling={isMediumWidth ? "outline" : "outline"}
            size={isMediumWidth ? 32 : 24}
            shade={ButtonShade.Gray}
            className="flex-none"
          >
            Get started
          </Button>
          <Popover.Button
            className="-my-8 md:hidden" // neg margin to not expand button height next to this.
            as={IconButton}
            shade={IconButtonShade.Blue}
            Icon={MenuIcon}
            size={40}
          />
        </div>
      </div>
      {/* Mobile panel */}
      <Transition
        as={Fragment}
        enter="duration-200 ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="duration-100 ease-in"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Popover.Panel
          focus
          className="absolute inset-x-0 top-0 bottom-0 z-[60] origin-top-right transform transition md:hidden"
        >
          {({ close }) => <MobileMenu links={headerLinks} close={close} />}
        </Popover.Panel>
      </Transition>
    </Popover>
  );
};

const HeaderDropdown = ({
  name,
  items,
}: {
  name: string;
  items: WebsiteLink[];
}) => {
  return (
    <Popover.Group as="nav" className="hidden md:flex">
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button
              as={Button}
              styling="ghost"
              square
              active={open}
              IconRight={ChevronDownIcon}
            >
              {name}
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-10 mt-12 -ml-16 w-screen max-w-md transform px-4 sm:px-0 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2">
                <div className="overflow-hidden rounded-lg border border-gray-400 bg-white shadow-b">
                  <div className="relative grid gap-12 bg-white px-8 py-12">
                    {items.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="flex  items-start gap-12 rounded px-12 py-8  hover:bg-gray-200"
                      >
                        {item.Icon && (
                          <item.Icon
                            className="h-32 w-32 flex-shrink-0"
                            aria-hidden="true"
                          />
                        )}
                        <div className="ml-4 flex flex-col gap-4">
                          <p className="text-14-16 text-gray-900">
                            {item.name}
                          </p>
                          <p className=" text-14-16 text-gray-600">
                            {item.description}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </Popover.Group>
  );
};

const MobileMenu = ({
  links,
  close,
}: {
  links: HeaderItem[];
  close: () => void;
}) => {
  const [currentLinks, setCurrentLinks] = useState<HeaderItem[]>(links);

  return (
    <div className="flex h-full flex-col justify-between gap-20 bg-blue ">
      <div>
        <div className="flex items-center justify-between p-12 ">
          <div>
            <Button
              IconLeft={ArrowLeftIcon}
              size={40}
              square
              styling="ghost"
              className={classNames(
                "text-white hover:border-blue hover:text-white",
                JSON.stringify(currentLinks) === JSON.stringify(links)
                  ? "hidden"
                  : ""
              )}
              onClick={() => setCurrentLinks(links)}
            >
              Back
            </Button>
          </div>
          <Popover.Button
            as={IconButton}
            Icon={XIcon}
            size={40}
            square
            className=" text-white hover:border-blue hover:text-blue"
          />
        </div>
        <div className="mt-20 px-12">
          <nav className="grid gap-y-4">
            {currentLinks.map((link) =>
              isNestedLink(link) ? (
                <button
                  key={link.name}
                  onClick={() => {
                    setCurrentLinks(link?.items);
                  }}
                  className="flex items-center gap-8 border border-transparent px-12 py-6 text-left text-32-40 font-bold tracking-wide text-white transition-all hover:gap-24 hover:border-white hover:text-white"
                >
                  {link.name} <ArrowRightIcon className="h-32 w-32" />
                </button>
              ) : (
                <Link key={link.name} href={link.href as string}>
                  <a
                    target={link.external ? "_blank" : undefined}
                    onClick={() => {
                      close();
                    }}
                    className="flex items-center gap-8 border border-transparent px-12 py-6 text-left text-32-40 font-bold tracking-wide text-white hover:border-white hover:text-white"
                  >
                    {link.name}
                    {link.external && (
                      <ArrowUpRightIcon className="h-32 w-32 text-white" />
                    )}
                  </a>
                </Link>
              )
            )}
          </nav>
        </div>
      </div>
      <div className="mx-24 mb-32">
        <Button
          block
          size={52}
          styling="outline"
          square
          href={HUMANLOOP_APP_LOGIN}
          className="border-blue-500 text-white hover:text-white"
        >
          Sign in
        </Button>
      </div>
    </div>
  );
};
